import { useState } from "react";
import { ChatTextIcon, TrashIcon } from "@phosphor-icons/react";

import api, { formatApiError } from "../../lib/api";
import {
  ErrorBanner,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextArea,
} from "../crm/Form";

const NOTE_TYPE_STYLES = {
  internal: "bg-slate-900 text-white",
  field: "bg-emerald-100 text-emerald-900 border border-emerald-300",
  customer_visible: "bg-amber-100 text-amber-900 border border-amber-300",
};

export default function NotesPanel({
  workOrderId,
  notes,
  canCreate,
  canCreateInternal,
  canDelete,
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [type, setType] = useState("field");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const types = [
    { value: "field", label: "Field" },
    { value: "customer_visible", label: "Customer Visible" },
    ...(canCreateInternal
      ? [{ value: "internal", label: "Internal (office)" }]
      : []),
  ];

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post(`/work-orders/${workOrderId}/notes`, {
        note_type: type,
        body,
      });
      setBody("");
      setType("field");
      setOpen(false);
      onChange();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setErr("");
    try {
      await api.delete(`/work-orders/${workOrderId}/notes/${id}`);
      onChange();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  return (
    <div data-testid="notes-panel" className="space-y-3">
      <ErrorBanner message={err} />
      {notes.length === 0 && !open && (
        <div className="text-xs text-slate-500 py-2">No notes yet.</div>
      )}
      <ul className="space-y-2">
        {notes.map((n) => (
          <li
            key={n.id}
            data-testid={`note-${n.id}`}
            className="bg-white border border-slate-200 rounded-sm p-3"
          >
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span
                data-testid={`note-type-${n.id}`}
                className={`inline-flex items-center rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] ${
                  NOTE_TYPE_STYLES[n.note_type] || ""
                }`}
              >
                {n.note_type.replace(/_/g, " ")}
              </span>
              <div className="flex items-center gap-2">
                <span className="font-data text-[10px] text-slate-500 tabular">
                  {new Date(n.created_at).toLocaleString()}
                </span>
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => remove(n.id)}
                    data-testid={`note-delete-${n.id}`}
                    className="text-rose-600 hover:text-rose-800"
                    aria-label="Delete note"
                  >
                    <TrashIcon size={12} />
                  </button>
                )}
              </div>
            </div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">
              {n.body}
            </div>
            {n.author_name && (
              <div className="font-data text-[10px] text-slate-500 mt-1.5">
                — {n.author_name}
              </div>
            )}
          </li>
        ))}
      </ul>

      {canCreate && (
        <div className="pt-1">
          {open ? (
            <form
              onSubmit={submit}
              className="space-y-3 border border-slate-200 rounded-sm p-3 bg-slate-50/60"
            >
              <Select
                value={type}
                onChange={(v) => setType(v || "field")}
                options={types}
                placeholder=""
                data-testid="note-type-select"
              />
              <TextArea
                required
                value={body}
                onChange={setBody}
                rows={3}
                placeholder="Add a note…"
                data-testid="note-body-input"
              />
              <div className="flex justify-end gap-2">
                <SecondaryButton
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    setBody("");
                  }}
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={busy}
                  data-testid="note-submit"
                >
                  {busy ? "Saving…" : "Add Note"}
                </PrimaryButton>
              </div>
            </form>
          ) : (
            <button
              type="button"
              data-testid="note-add-button"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 font-data text-[10px] uppercase tracking-[0.18em] text-slate-700 hover:text-amber-700"
            >
              <ChatTextIcon size={12} weight="bold" /> Add Note
            </button>
          )}
        </div>
      )}
    </div>
  );
}
